/**
 * Live character budget for one variant, counted the way the platform counts
 * (X collapses every URL to 23 characters). Blogs get a word/reading estimate
 * instead of a limit.
 */

import { PLATFORM_SPECS, countChars } from '../../../shared/platforms';
import type { PlatformId } from '../../../shared/types';
import { readingMinutes, stripHtml, wordCount } from '../../../shared/utils';
import { cn } from '../../lib/utils';

export interface CharCounterProps {
  platform: PlatformId;
  text: string;
  className?: string;
}

export function CharCounter({ platform, text, className }: CharCounterProps) {
  const spec = PLATFORM_SPECS[platform];

  if (spec.limits.unlimitedChars) {
    const words = wordCount(text);
    return (
      <span className={cn('pap-tnum text-xs text-subtle', className)}>
        {words.toLocaleString()} word{words === 1 ? '' : 's'} · {readingMinutes(stripHtml(text))} min
        read
      </span>
    );
  }

  const used = countChars(platform, text);
  const max = spec.limits.maxChars;
  const remaining = max - used;
  const ratio = Math.min(1, used / max);
  const over = remaining < 0;
  const close = !over && remaining <= Math.max(20, max * 0.1);

  const size = 18;
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <span
      className={cn('inline-flex items-center gap-1.5', className)}
      title={`${used.toLocaleString()} of ${max.toLocaleString()} characters`}
    >
      <svg width={size} height={size} className="-rotate-90 shrink-0" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-sunken"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - (over ? 1 : ratio))}
          className={cn(
            'transition-[stroke-dashoffset] duration-300 ease-out',
            over ? 'stroke-danger' : close ? 'stroke-warning' : 'stroke-accent',
          )}
        />
      </svg>
      <span
        className={cn(
          'pap-tnum text-xs',
          over ? 'font-medium text-danger' : close ? 'text-warning' : 'text-subtle',
        )}
      >
        {over ? `${Math.abs(remaining).toLocaleString()} over` : remaining.toLocaleString()}
      </span>
    </span>
  );
}
